<template>
  <v-container
    class="d-flex justify-center align-center min-h-screen bg-grey-lighten-4 py-5"
  >
    <v-card class="pa-6 rounded-lg elevation-6 w-full max-w-2xl">
      <v-card-title
        class="text-h4 font-weight-bold text-center mb-6 text-blue-darken-3"
      >
        My User Profile Report
      </v-card-title>

      <v-card-text>
        <!-- Loading State -->
        <div v-if="isLoadingAuth" class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="blue-darken-2"
            size="64"
            width="6"
          ></v-progress-circular>
          <p class="mt-4 text-subtitle-1 text-grey-darken-1">
            Loading user authentication and profile data...
          </p>
        </div>

        <!-- Generation Error State -->
        <v-alert
          v-else-if="genError"
          type="error"
          icon="mdi-alert-circle"
          class="mb-6 rounded-lg"
        >
          {{ genError }}
        </v-alert>

        <!-- Not Authenticated State -->
        <v-alert
          v-else-if="!isAuthenticated"
          type="warning"
          icon="mdi-account-off"
          class="mb-6 rounded-lg"
        >
          You must be logged in to generate your profile PDF.
          <br />Please log in to continue.
        </v-alert>

        <!-- Display User Data if Available -->
        <div v-else-if="user">
          <v-list lines="two" class="rounded-lg border">
            <v-list-item>
              <template v-slot:prepend>
                <v-icon color="blue-darken-1">mdi-account</v-icon>
              </template>
              <v-list-item-title class="font-weight-medium"
                >Name</v-list-item-title
              >
              <v-list-item-subtitle>{{
                user.name || "N/A"
              }}</v-list-item-subtitle>
            </v-list-item>

            <v-divider inset></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon color="blue-darken-1">mdi-email</v-icon>
              </template>
              <v-list-item-title class="font-weight-medium"
                >Email</v-list-item-title
              >
              <v-list-item-subtitle>{{
                user.email || "N/A"
              }}</v-list-item-subtitle>
            </v-list-item>

            <v-divider inset></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon color="blue-darken-1">mdi-domain</v-icon>
              </template>
              <v-list-item-title class="font-weight-medium"
                >Department</v-list-item-title
              >
              <v-list-item-subtitle>{{
                user.department || "N/A"
              }}</v-list-item-subtitle>
            </v-list-item>

            <v-divider inset></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon color="blue-darken-1">mdi-briefcase</v-icon>
              </template>
              <v-list-item-title class="font-weight-medium"
                >Role</v-list-item-title
              >
              <v-list-item-subtitle>{{
                user.role || "N/A"
              }}</v-list-item-subtitle>
            </v-list-item>

            <v-divider inset></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon color="blue-darken-1">mdi-calendar-plus</v-icon>
              </template>
              <v-list-item-title class="font-weight-medium"
                >Account Created</v-list-item-title
              >
              <v-list-item-subtitle>
                {{
                  user.createdAt
                    ? new Date(user.createdAt.seconds * 1000).toLocaleString()
                    : "N/A"
                }}
              </v-list-item-subtitle>
            </v-list-item>

            <v-divider inset></v-divider>

            <v-list-item>
              <template v-slot:prepend>
                <v-icon color="blue-darken-1">mdi-clock-outline</v-icon>
              </template>
              <v-list-item-title class="font-weight-medium"
                >Last Login</v-list-item-title
              >
              <v-list-item-subtitle>
                {{
                  user.lastlogin
                    ? new Date(user.lastlogin.seconds * 1000).toLocaleString()
                    : "N/A"
                }}
              </v-list-item-subtitle>
            </v-list-item>

            <v-divider inset v-if="user.photoUrl"></v-divider>

            <v-list-item v-if="user.photoUrl">
              <template v-slot:prepend>
                <v-icon color="blue-darken-1">mdi-image</v-icon>
              </template>
              <v-list-item-title class="font-weight-medium"
                >Profile Photo</v-list-item-title
              >
              <v-list-item-subtitle>
                <a
                  :href="user.photoUrl"
                  target="_blank"
                  class="text-blue-accent-2"
                  >View Photo Link</a
                >
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </div>
        <!-- Fallback if no user data -->
        <div v-else class="text-center py-8">
          <v-icon size="64" color="bg-grey-lighten-1"
            >mdi-account-question</v-icon
          >
          <p class="mt-4 text-subtitle-1 text-grey-darken-1">
            User profile data not available. Please ensure you are logged in and
            your profile exists in Firestore.
          </p>
        </div>
      </v-card-text>

      <v-card-actions class="justify-center mt-6">
        <v-btn
          color="blue-darken-2"
          size="large"
          variant="flat"
          class="rounded-pill px-8 py-3 text-white"
          @click="generatePDF"
          :disabled="!user || isLoadingAuth || !isAuthenticated"
        >
          <v-icon left>mdi-file-pdf-box</v-icon>
          Generate PDF Report
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref } from "vue";
import { jsPDF } from "jspdf";

// These imports will execute the font registration code contained within them.
// Ensure .js extension is included and paths are correct.
import "@/assets/font/Sarabun-Regular-normal.js";
import "@/assets/font/Sarabun-Bold-bold.js";

import { useAuthStore } from "@/stores/auth";
import { storeToRefs } from "pinia";

const authStore = useAuthStore();
const { user, isLoadingAuth, isAuthenticated } = storeToRefs(authStore);

const genError = ref(null);

const generatePDF = () => {
  genError.value = null;
  if (!user.value) {
    genError.value = "No user profile found. Please log in.";
    alert(genError.value);
    return;
  }

  const doc = new jsPDF("p", "mm", "a4");

  // --- DIAGNOSTIC STEP: Log registered fonts ---
  console.log("jsPDF Registered Fonts:", doc.getFontList());
  // ---------------------------------------------

  // --- FIX: Use the EXACT font family names as registered by the .js files ---
  // Based on your console log, the font family names are 'Sarabun-Regular' and 'Sarabun-Bold'.
  // The style is still 'normal' or 'bold'.
  doc.setFont("Sarabun-Regular", "normal"); // Set default font for the document to Sarabun-Regular normal
  doc.setFontSize(11);
  let yOffset = 20;

  doc.setFontSize(22);
  doc.setTextColor(50, 50, 150);
  doc.setFont("Sarabun-Bold", "bold"); // Use Sarabun-Bold bold for the title
  doc.text("User Profile Report", 105, yOffset, { align: "center" });
  yOffset += 15;

  doc.setDrawColor(150, 150, 150);
  doc.line(20, yOffset, 190, yOffset);
  yOffset += 10;

  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);

  const addfield = (label, value) => {
    // Set font for labels (bold Sarabun)
    doc.setFont("Sarabun-Bold", "bold"); // Use Sarabun-Bold for bold labels
    doc.text(`${label}:`, 20, yOffset);

    // Set font for values (normal Sarabun)
    doc.setFont("Sarabun-Regular", "normal"); // Use Sarabun-Regular for normal values
    doc.text(String(value || "N/A"), 70, yOffset);
    yOffset += 8;
  };

  addfield("Name", user.value.name);
  addfield("Email", user.value.email);
  addfield("Department", user.value.department);
  addfield("Role", user.value.role);

  const createdDate = user.value.createdAt
    ? new Date(user.value.createdAt.seconds * 1000).toLocaleString()
    : "N/A";
  addfield("Created Date", createdDate);

  const lastLoginDate = user.value.lastlogin
    ? new Date(user.value.lastlogin.seconds * 1000).toLocaleString()
    : "N/A";
  addfield("Last Login", lastLoginDate);

  if (user.value.photoUrl) {
    addfield("Profile Photo URL", user.value.photoUrl);
  }

  // Footer - also set to Sarabun-Regular
  doc.setFont("Sarabun-Regular", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 280);
  doc.text(`Page 1 of 1`, 190, 280, { align: "right" });

  const fileName = `${user.value.name || "User"}-Profile-Report.pdf`;
  doc.save(fileName);
};
</script>

<style scoped>
/* Your existing styles */
.min-h-screen {
  min-height: 100vh;
}

.v-card {
  border: 1px solid #e0e0e0;
}

.v-list-item-title {
  color: #3f51b5;
}

.v-list-item-subtitle {
  color: #616161;
}

.v-btn {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease-in-out;
}

.v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.3);
}
</style>
